import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import { NewInput } from '../../../../Components/PIM/common';
import useDeferredRowChange from '../../../../Components/BombiixAGGrid/useDeferredRowChange';
import type { BombiixCellRendererParams, BombiixRowId } from '../../../../Components/BombiixAGGrid';
import { TableRow } from './types';
import { useProductLandingPath } from '../../../../Components/CustomHooks/useProductLandingPath';

interface NumberCellExtras {
  /** True when the row's Product Number Type is Auto-generated. */
  isAuto?: (row: TableRow) => boolean;
  /** True when an active auto-id config exists (so the generate icon can appear). */
  autoIdActive?: boolean;
  /** Generate the auto product number for this row; returns the generated string (or ''). */
  onGenerate?: (row: TableRow) => Promise<string | undefined>;
  /** True only on the inline add/edit row — list rows render the number as a link instead of an input. */
  isInlineRow?: (row: TableRow) => boolean;
}

type NumberCellProps = BombiixCellRendererParams<TableRow, string> & NumberCellExtras;

/**
 * Product Number cell for the table view. Mirrors the CreateProduct page: a text input
 * plus, when the row's Product Number Type is Auto-generated (and an active auto-id config
 * exists), a refresh icon that generates the number. Manual rows show a plain input.
 * The input is disabled while in auto mode, exactly like the create page.
 */
function NumberCell(params: NumberCellProps) {
  const { value, data, colDef, onRowChange, getRowId, placeholder, disabled, readOnly, error, isAuto, autoIdActive, onGenerate, isInlineRow } = params;
  const rowId: BombiixRowId | undefined = getRowId && data ? getRowId(data) : (data as { id?: BombiixRowId } | undefined)?.id;
  const field = colDef?.field;
  const editable = Boolean(data && isInlineRow?.(data));

  const focusedRef = useRef<boolean>(false);
  const [local, setLocal] = useState<string>(value ?? '');
  const [generating, setGenerating] = useState<boolean>(false);
  const [commitChange, flushChange] = useDeferredRowChange(onRowChange);
  const productLandingPath = useProductLandingPath();

  useEffect(() => {
    if (!focusedRef.current) setLocal(value ?? '');
  }, [value]);

  const auto = Boolean(data && isAuto?.(data));
  const showIcon = auto && Boolean(autoIdActive);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const nextValue = e.target.value;
    setLocal(nextValue);
    if (rowId !== undefined && field) commitChange(rowId, field, nextValue);
  };

  const handleGenerate = async () => {
    if (!data || !onGenerate || generating) return;
    setGenerating(true);
    try {
      const generated = await onGenerate(data);
      if (generated) setLocal(generated);
    } finally {
      setGenerating(false);
    }
  };

  /* List rows show the product number as a link that opens the product (same target + classes the
     card view's title uses: /development-order/:id/product-identifier). Only the inline add/edit row
     keeps the editable input + auto-id generate icon. Returned AFTER all hooks so hook order stays
     stable across list vs inline rows (Rules of Hooks). */
  if (!editable && data) {
    if (!local) return <div className="cag-cell cag-cell-readonly" />;
    return (
      <div className="cag-cell cag-cell-readonly">
        <Link className="ptv-number-link" title={local} to={productLandingPath(String(data._id))}>
          {local}
        </Link>
      </div>
    );
  }

  return (
    <div className={`cag-cell-wrap${error ? ' cag-cell-wrap--error' : ''}`}>
      <div className="cag-cell ptv-number-cell d-flex align-items-center position-relative">
        <NewInput
          className="cag-cell-input"
          type="text"
          invalid={Boolean(error)}
          value={local}
          placeholder={placeholder ?? colDef?.headerName ?? 'Product Number'}
          disabled={disabled || auto}
          readOnly={readOnly}
          onFocus={() => {
            focusedRef.current = true;
          }}
          onBlur={() => {
            focusedRef.current = false;
            flushChange();
            setLocal(value ?? '');
          }}
          onChange={handleChange}
        />
        {showIcon && (
          <button
            type="button"
            className="ptv-auto-id-btn"
            onClick={handleGenerate}
            disabled={generating}
            aria-label="Generate Product Number"
            title="Generate Product Number"
          >
            <i className={`icon-Bulk_Update f-24 ptv-auto-id-icon${generating ? ' ptv-auto-id-icon--spin' : ''}`} />
          </button>
        )}
      </div>
      {error ? <span className="cag-cell-error">{error}</span> : null}
    </div>
  );
}

export default NumberCell;
